// Student agency-profile screen: fetch home, upload intro video and photos, sync video player state.
// Fixed: upload handlers must clear isOnNetworking on throw; intro URL used wrong key introVideoInfo (home uses videoInfo).
import React, { useEffect, useRef, useState } from "react";
import AgencyProfileComponent from "./AgencyProfile.component";
import { useLocation, useNavigate } from "react-router-dom";

import * as AgencyProfileApi from "api/student/v2/agencyProfile.api";
import * as ImageHelper from "helpers/Image.helper";
import * as DataApi from "api/common/Data.api";
import * as AgencyApi from "api/agency/Agency.api";
import { increaseStudentIntroVideoViewCount } from "api/agency/v2/Student.api";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import type { VideoUploadFlowPhase } from "commons/popup/VideoUploadFlowPopup.component";
import { useVideoUploadFlow } from "commons/popup/useVideoUploadFlow";

function formatUnknownIntroVideoError(e: unknown): string {
  if (e instanceof Error && e.message) return e.message;
  return "Upload failed. Please try again.";
}

async function getVideoMetadataViaElement(file: File): Promise<{
  duration: number;
  videoWidth: number;
  videoHeight: number;
} | null> {
  // Android 15 file picker can fail metadata parsing in some helpers; using <video> metadata is more robust.
  const objectUrl = URL.createObjectURL(file);
  try {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;

    const meta = await new Promise<{
      duration: number;
      videoWidth: number;
      videoHeight: number;
    } | null>((resolve) => {
      const timeoutId = window.setTimeout(() => resolve(null), 7000);

      video.onloadedmetadata = () => {
        window.clearTimeout(timeoutId);
        resolve({
          duration: Number.isFinite(video.duration) ? video.duration : 0,
          videoWidth: video.videoWidth || 0,
          videoHeight: video.videoHeight || 0,
        });
      };
      video.onerror = () => {
        window.clearTimeout(timeoutId);
        resolve(null);
      };

      video.src = objectUrl;
    });

    return meta;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function dataUrlToFile(dataUrl: string, fileName: string): File | null {
  try {
    const [meta, base64] = dataUrl.split(",");
    if (!meta || !base64) return null;
    const mimeMatch = /data:(.*?);base64/.exec(meta);
    const mime = mimeMatch?.[1] || "image/jpeg";
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], fileName, { type: mime });
  } catch {
    return null;
  }
}

async function extractVideoThumbnailCandidates(
  file: File,
  count = 8
): Promise<string[]> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const video = document.createElement("video");
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    video.src = objectUrl;
    video.crossOrigin = "anonymous";

    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error("video metadata load failed"));
    });

    const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 1;
    const maxFrames = Math.max(1, count);
    const times = Array.from({ length: maxFrames }, (_, idx) => {
      if (maxFrames === 1) return 0;
      const ratio = idx / (maxFrames - 1);
      return Math.min(duration - 0.01, Math.max(0, duration * ratio));
    });

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, video.videoWidth || 640);
    canvas.height = Math.max(1, video.videoHeight || 360);
    const ctx = canvas.getContext("2d");
    if (!ctx) return [];

    const frames: string[] = [];
    for (const t of times) {
      await new Promise<void>((resolve) => {
        const done = () => resolve();
        video.onseeked = done;
        try {
          video.currentTime = t;
        } catch {
          resolve();
        }
      });
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        frames.push(canvas.toDataURL("image/jpeg", 0.88));
      } catch {
        /* ignore frame */
      }
    }
    return frames.filter(Boolean);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

const AgencyProfileContainer = (props: any) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(location.state?.tab || "profile");
  const [profileSubTab, setProfileSubTab] = useState(
    location.state?.profileSubTab || "main"
  );

  const [profile, setProfile] = useState(null);
  const [isProfileActive, setIsProfileActive] = useState(false);
  const [isOnChangeProfileActive, setIsOnChangeProfileActive] = useState(false);

  const [videoInfo, setVideoInfo] = useState(null);
  const [faceFrontImageInfo, setFaceFrontImageInfo] = useState(null);
  const [faceLeftImageInfo, setFaceLeftImageInfo] = useState(null);
  const [faceRightImageInfo, setFaceRightImageInfo] = useState(null);

  const [fullBodyFrontImageInfo, setFullBodyFrontImageInfo] = useState(null);
  const [fullBodyLeftImageInfo, setFullBodyLeftImageInfo] = useState(null);
  const [fullBodyRightImageInfo, setFullBodyRightImageInfo] = useState(null);


  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [practiceList, setPracticeList] = useState(null);
  const [isPracticeLoading, setIsPracticeLoading] = useState(true);
  const inputRef = useRef<any>();

  const [isOnNetworking, setIsOnNetworking] = useState(false);
  const videoUploadFlow = useVideoUploadFlow();
  const videoRef = useRef(null);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const [initFlag, setInitFlag] = useState(false);

  const [isShowPopup, setIsShowPopup] = useState(false);
  const [popupList, setPopupList] = useState([]);
  const [profileMediaType, setProfileMediaType] = useState("");
  const [isAgencyPopup, setIsAgencyPopup] = useState(false);
  const [agencyInfo, setAgencyInfo] = useState(null);

  const searchParams = new URLSearchParams(location.search);
  const limit = 8;
  const [page, setPage] = useState(
    location.state?.page || +searchParams.get("page") || 1
  );

  const [totalCount, setTotalcount] = useState(0);

  useEffect(() => {
    fetchHomeInfo();
  }, []);

  useEffect(() => {
    const updateInfo = async () => {
      if (profile.videoInfo) {
        const data = await DataApi.getVideoPresignedUrl(profile.videoInfo.videoId);
        if (data?.result) {
          setVideoInfo({
            ...profile.videoInfo,
            // Align shape with commons/video/VideoPlayer expectation.
            id: profile.videoInfo.id || profile.videoInfo.videoId,
            thumbnailImage:
              profile.videoInfo.thumbnailImage ||
              profile.videoInfo.thumbnailImageInfo ||
              null,
            url: data.result,
          });
          console.log("videoUrl : ", data.result)
          // captureThumbnail(data.result, 0);
        }
      }

      if (profile.faceFrontImageInfo) {
        if (profile.faceFrontImageInfo.imageId) {
          const frontInfo = await DataApi.getImagePresignedUrl(profile.faceFrontImageInfo.imageId);
          if (frontInfo?.result) {
            setFaceFrontImageInfo({ ...profile.faceFrontImageInfo, url: frontInfo.result });
          }
        }
      }

      if (profile.faceLeftImageInfo) {
        if (profile.faceLeftImageInfo.imageId) {
          const leftInfo = await DataApi.getImagePresignedUrl(profile.faceLeftImageInfo.imageId);
          if (leftInfo?.result) {
            setFaceLeftImageInfo({ ...profile.faceLeftImageInfo, url: leftInfo.result });
          }
        }
      }

      if (profile.faceRightImageInfo) {
        if (profile.faceRightImageInfo.imageId) {
          const rightInfo = await DataApi.getImagePresignedUrl(profile.faceRightImageInfo.imageId);
          if (rightInfo?.result) {
            setFaceRightImageInfo({ ...profile.faceRightImageInfo, url: rightInfo.result });
          }
        }
      }

      if (profile.fullBodyFrontImageInfo) {
        if (profile.fullBodyFrontImageInfo.imageId) {
          const frontInfo = await DataApi.getImagePresignedUrl(profile.fullBodyFrontImageInfo.imageId);
          if (frontInfo?.result) {
            setFullBodyFrontImageInfo({ ...profile.fullBodyFrontImageInfo, url: frontInfo.result });
          }
        }
      }

      if (profile.fullBodyLeftImageInfo) {
        if (profile.fullBodyLeftImageInfo.imageId) {
          const leftInfo = await DataApi.getImagePresignedUrl(profile.fullBodyLeftImageInfo.imageId);
          if (leftInfo?.result) {
            setFullBodyLeftImageInfo({ ...profile.fullBodyLeftImageInfo, url: leftInfo.result });
          }
        }
      }

      if (profile.fullBodyRightImageInfo) {
        if (profile.fullBodyRightImageInfo.imageId) {
          const rightInfo = await DataApi.getImagePresignedUrl(profile.fullBodyRightImageInfo.imageId);
          if (rightInfo?.result) {
            setFullBodyRightImageInfo({ ...profile.fullBodyRightImageInfo, url: rightInfo.result });
          }
        }
      }
    }

    if (profile) {
      updateInfo();
    }

  }, [profile])

  const fetchHomeInfo = async () => {
    const result = await AgencyProfileApi.getHomeInfo()
      .then((data: any) => {
        if (data?.info) {
          setProfile(data.info);
          setIsProfileActive(data.info.isProfileActive);
        }
      })
      .catch((err: any): null => {
        console.log(err);
        return null;
      });
  };

  const getPractice = async () => {
    setIsPracticeLoading(true);
    const result = await AgencyProfileApi.getListPractice({ page, limit });

    if (result?.list) {
      const updatedList = await Promise.all(
        result.list.map(async (video: any) => {
          const videoInfo = await DataApi.getVideoPresignedUrl(video.videoId);
          if (videoInfo) {
            return {
              ...video,
              url: videoInfo.result,
            };
          }
          return video;
        })
      );

      setPracticeList(updatedList);
      setTotalcount(result.totalCount);
    }
    setIsPracticeLoading(false);
  };

  const captureThumbnail = (videoUrl: string, idx: number) => {
    const videoElement = document.createElement("video");
    videoElement.src = videoUrl;
    videoElement.crossOrigin = "anonymous";
    videoElement.muted = true;
    videoElement.playsInline = true;

    videoElement.addEventListener("loadeddata", () => {
      videoElement.currentTime = 0;
    });

    videoElement.addEventListener("seeked", () => {
      const canvas = document.getElementById(
        `canvas-${idx}`
      ) as HTMLCanvasElement;

      if (canvas) {
        const context = canvas.getContext("2d");

        if (context) {
          canvas.width = videoElement.videoWidth;
          canvas.height = videoElement.videoHeight;

          context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

          const imgElement = document.getElementById(
            `thumbnail-${idx}`
          ) as HTMLImageElement;
          imgElement.src = canvas.toDataURL("image/png");
          imgElement.style.visibility = "visible";
        }
      }
    });
    videoElement.load();
  };

  const uploadImage = async (evt: any, category: string, type: string) => {
    if (!isOnNetworking) {
      setIsOnNetworking(true);

      if (!evt) {
        setIsOnNetworking(false);
        return false;
      }

      const input = evt.target as HTMLInputElement;

      try {
        const file = input.files?.[0];
        if (!file) {
          return false;
        }

        const imageInfo = await ImageHelper.getImageInfo(file);
        if (!imageInfo) {
          alert("Please choose image file");
          return false;
        }

        const result = await DataApi.uploadImage(file, false);
        imageInfo.id = result.id;

        const upload = await AgencyProfileApi.createProfileImage({
          imageId: result.id,
          profileImageCategoryCode: category,
          profileImageTypeCode: type,
        });

        if (upload && upload.status === 200) {
          await fetchHomeInfo();
        }
      } catch (e) {
        console.error(e);
        alert(
          e instanceof Error ? e.message : "Upload failed. Please try again."
        );
      } finally {
        setIsOnNetworking(false);
        input.value = "";
      }
    }
  };

  const agencyProfileTxt = (key: string, fallback: string) =>
    getLanguageValue(PAGE_TYPE.STUDENT, `agencyProfile.${key}`) || fallback;
  const invalidUploadFileAlert = () =>
    alert(
      agencyProfileTxt(
        "uploadIntroVideoInvalidSelectableFile",
        "This file cannot be uploaded."
      )
    );

  const handleCancelIntroVideoUpload = () => {
    videoUploadFlow.reset();
    setIsOnNetworking(false);
    const el = document.getElementById("input-file-video") as HTMLInputElement | null;
    if (el) el.value = "";
  };

  const handleCloseIntroVideoPopup = () => {
    videoUploadFlow.reset();
    setIsOnNetworking(false);
    const el = document.getElementById("input-file-video") as HTMLInputElement | null;
    if (el) el.value = "";
  };

  const handleSelectIntroVideoThumbnail = (index: number) => {
    videoUploadFlow.selectThumbnail(index);
  };

  const handleSaveIntroVideoWithThumbnail = async () => {
    const uploadErrorText = agencyProfileTxt(
      "uploadIntroVideoRegisterFailed",
      "Could not save the intro video."
    );
    try {
      const saved = await videoUploadFlow.saveSelectedThumbnail(uploadErrorText);
      const videoId = saved?.videoId;
      if (!videoId) return;
      const upload = await AgencyProfileApi.createIntroVideo({ videoId });
      if (upload && upload.status === 200) {
        await fetchHomeInfo();
        handleCloseIntroVideoPopup();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const uploadVideo = async (evt: any) => {
    if (isOnNetworking) {
      invalidUploadFileAlert();
      return false;
    }
    if (!evt?.target) {
      invalidUploadFileAlert();
      return false;
    }
    const input = evt.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !file.type?.startsWith("video/")) {
      invalidUploadFileAlert();
      input.value = "";
      return false;
    }

    setIsOnNetworking(true);
    try {
      const meta = await getVideoMetadataViaElement(file);
      const width = meta?.videoWidth || 0;
      const height = meta?.videoHeight || 0;
      await videoUploadFlow.startUpload(file, width, height, {
        preparing: agencyProfileTxt("uploadIntroVideoPreparing", "Preparing upload..."),
        uploading: agencyProfileTxt("uploadIntroVideoFile", "Uploading video file..."),
        uploadError: agencyProfileTxt(
          "uploadIntroVideoInvalidSelectableFile",
          "This file cannot be uploaded."
        ),
        thumbnailing: agencyProfileTxt(
          "uploadIntroVideoPleaseWait",
          "Please wait"
        ),
      });
      return true;
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      setIsOnNetworking(false);
    }
  };

  const getVideoUrl = async (id: string) => {
    if (id) {
      const videoInfo = await DataApi.getVideoPresignedUrl(id);
      if (videoInfo) {
        const url = videoInfo.result;
        return url;
      }
    }
  };

  const handleEnded = () => {
    console.log('ended')
    setCurrentTime(0);
  };

  const handleSeeked = () => {
    console.log('seek')
    const video = videoRef.current;
    if (video) {
      setCurrentTime(video.currentTime);
      video.play();
    }
  };

  const handlePlay = () => {
    console.log('play')
    setInitFlag(false);
  };

  const handlePause = () => {
    console.log('pause')
    const video = videoRef.current;
    if (video) {
      setCurrentTime(video.currentTime);
    }
    setInitFlag(true);
  };

  const openViewPopup = async (
    profileMediaId: string,
    profileMediaType: string
  ) => {
    if (profileMediaId) {
      const result = await AgencyProfileApi.getListPraciceViewedAgency({
        profileMediaId: profileMediaId,
        profileMediaType: profileMediaType,
      });

      if (result.list) {
        setPopupList(result.list);
      }
      setProfileMediaType(profileMediaType);
      setIsShowPopup(true);
    }
  };

  const openAgencyPopup = async (id: number) => {
    const result = await AgencyApi.getInfo(id);
    if (result.info) {
      setAgencyInfo(result.info);
    }
    setIsAgencyPopup(true);
  };

  useEffect(() => {
    const handleInitFlagChange = async () => {
      if (!initFlag) {
        const videoId = profile?.videoInfo?.videoId;
        const url = await getVideoUrl(videoId);
        setSelectedVideoUrl(url ?? "");
        setInitFlag(false);
      }
    };

    handleInitFlagChange();
  }, [initFlag]);

  useEffect(() => {
    const video = videoRef.current;

    if (video) {
      video.src = selectedVideoUrl;
      video.muted = true;
      video.playsInline = true;
      video.currentTime = currentTime;
      video.load();
      const handleLoadedData = () => {
        //video.play().catch((e: any) => console.log(e));
      };

      const handleError = () => {
        console.log(45454545)
      }

      video.addEventListener("loadeddata", handleLoadedData);
      video.addEventListener("play", handlePlay);
      video.addEventListener("pause", handlePause);
      video.addEventListener("ended", handleEnded);
      video.addEventListener("seeked", handleSeeked);
      video.addEventListener("onerror", handleError);

      return () => {
        video.removeEventListener("loadeddata", handleLoadedData);
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePause);
        video.removeEventListener("ended", handleEnded);
        video.removeEventListener("seeked", handleSeeked);
      };
    }
  }, [selectedVideoUrl]);

  const practiceInit = async () => {
    await getPractice();
  };

  const handleChangeIsProfileActive = async (checked: boolean) => {

    if (isOnChangeProfileActive) {
      return;
    }

    setIsOnChangeProfileActive(true);

    await AgencyProfileApi.updateIsActiveOfAgencyProfile({ isProfileActive: checked })
      .then((res: any) => {
        console.log(res)
        console.log({ checked });
        setIsProfileActive(checked);
      }).catch((err: any) => {
        console.log(err)
        console.log({ checked });
        setIsProfileActive(!checked);
      }).finally(() => {
        setIsOnChangeProfileActive(false);
      })
  };

  const introVideoUploadFlow = {
    isVisible: videoUploadFlow.state.isVisible,
    phase: videoUploadFlow.state.phase as VideoUploadFlowPhase,
    progress: videoUploadFlow.state.progress,
    currentStep: videoUploadFlow.state.currentStep,
    canCancel: false,
    errorMessage: videoUploadFlow.state.errorMessage,
    thumbnails: videoUploadFlow.state.thumbnails,
    selectedIndex: videoUploadFlow.state.selectedIndex,
    isSaving: videoUploadFlow.state.isSaving,
  };

  return (
    <AgencyProfileComponent
      {...props}
      tab={tab}
      setTab={setTab}
      profileSubTab={profileSubTab}
      setProfileSubTab={setProfileSubTab}

      videoInfo={videoInfo}
      faceFrontImageInfo={faceFrontImageInfo}
      faceLeftImageInfo={faceLeftImageInfo}
      faceRightImageInfo={faceRightImageInfo}
      fullBodyFrontImageInfo={fullBodyFrontImageInfo}
      fullBodyLeftImageInfo={fullBodyLeftImageInfo}
      fullBodyRightImageInfo={fullBodyRightImageInfo}

      //
      profile={profile}
      isProfileLoading={isProfileLoading}
      setIsProfileLoading={setIsProfileLoading}
      practiceList={practiceList}
      isPracticeLoading={isPracticeLoading}
      //upload
      videoRef={videoRef}
      uploadVideo={uploadVideo}
      uploadImage={uploadImage}
      //
      isShowPopup={isShowPopup}
      setIsShowPopup={setIsShowPopup}
      popupList={popupList}
      openViewPopup={openViewPopup}
      profileMediaType={profileMediaType}
      //
      isAgencyPopup={isAgencyPopup}
      setIsAgencyPopup={setIsAgencyPopup}
      agencyInfo={agencyInfo}
      openAgencyPopup={openAgencyPopup}
      // pagination
      limit={limit}
      page={page}
      setPage={setPage}
      totalCount={totalCount}

      // 기획사용 프로필 활성화 여부
      isProfileActive={isProfileActive}
      onChangeIsProfileActive={handleChangeIsProfileActive}
      introVideoUploadFlow={introVideoUploadFlow}
      onCancelIntroVideoUpload={handleCancelIntroVideoUpload}
      onCloseIntroVideoUpload={handleCloseIntroVideoPopup}
      onSaveIntroVideoUpload={handleSaveIntroVideoWithThumbnail}
      onSelectIntroVideoUploadThumbnail={handleSelectIntroVideoThumbnail}
    />
  );
};

export default AgencyProfileContainer;
